import React, { useRef, useState } from "react";
import { Settings, formatTime, intervalToMinutes, moveBoundary, slices } from "../economyMode";

// Geometry in SVG units (the viewBox), as on mobile: a 227 ring with a 143 hole.
const OUTER_R = 113.5;
const INNER_R = 71.5;
const LABEL_R = OUTER_R + 24;
const VIEW = 2 * (LABEL_R + 22); // room for the hour marks outside the ring
const C = VIEW / 2;

// Pins: a white shaft cut through the band (it doubles as the divider between
// periods) with a two-tone disc head on the ring's outer edge.
const PIN_SHAFT_INNER = INNER_R - 2;
const PIN_SHAFT_OUTER = OUTER_R + 2;
const PIN_OUTER = 13.85;
const PIN_INNER = 10.55;

// A press within this many minutes of a boundary grabs it, if it's on the ring.
const GRAB_MINUTES = 60;
const GRAB_SLOP_INNER = 8;
const GRAB_SLOP_OUTER = 18;

const TONE_FOR_MODE: Record<number, string> = { 1: 'off', 2: 'mid', 3: 'peak' };

// Midnight at the top, noon at the bottom: the right side is morning, the left evening.
const HOUR_MARKS = [180, 360, 540, 900, 1080, 1260].map((minutes) => ({
    minutes,
    label: `${((minutes / 60) % 12) || 12}:00`,
}));

const polar = (r: number, minutes: number) => {
    const angle = ((minutes / 1440) * 360 - 90) * (Math.PI / 180);
    return { x: C + r * Math.cos(angle), y: C + r * Math.sin(angle) };
};

/** An annular wedge from `start` to `end` minutes, clockwise. */
const ringSlice = (start: number, end: number): string => {
    const span = (((end - start) % 1440) + 1440) % 1440;
    if (span === 0) return `${ringSlice(start, start + 720)} ${ringSlice(start + 720, start)}`;
    const large = span > 720 ? 1 : 0;
    const o1 = polar(OUTER_R, start);
    const o2 = polar(OUTER_R, end);
    const i2 = polar(INNER_R, end);
    const i1 = polar(INNER_R, start);
    return `M ${o1.x} ${o1.y} A ${OUTER_R} ${OUTER_R} 0 ${large} 1 ${o2.x} ${o2.y} L ${i2.x} ${i2.y} A ${INNER_R} ${INNER_R} 0 ${large} 0 ${i1.x} ${i1.y} Z`;
};

interface EconomyClockProps {
    settings: Settings;
    /** Drag grid in minutes; defaults to the half-hour grid. V1 spas pass whole hours. */
    snapMinutes?: number;
    editable: boolean;
    onChange: (next: Settings) => void;
}

/**
 * The 24-hour schedule ring. Read-only until `editable`; then each boundary gets
 * a pin that can be dragged (snapping to half-hours, never crossing its
 * neighbours). The centre shows the time while a pin is held.
 */
const EconomyClock = ({ settings, editable, onChange, snapMinutes }: EconomyClockProps) => {
    const svgRef = useRef<SVGSVGElement>(null);
    const [dragging, setDragging] = useState<number | null>(null);
    const [hovering, setHovering] = useState<number | null>(null);

    /** Pointer position in viewBox units. */
    const toView = (e: React.PointerEvent) => {
        const rect = svgRef.current!.getBoundingClientRect();
        const scale = VIEW / rect.width;
        return { x: (e.clientX - rect.left) * scale, y: (e.clientY - rect.top) * scale };
    };

    const minutesAt = (x: number, y: number) => {
        const deg = (Math.atan2(y - C, x - C) * 180) / Math.PI;
        const normalized = (((deg + 90) % 360) + 360) % 360;
        return Math.round((normalized / 360) * 1440);
    };

    /** The boundary a press would grab, or null. */
    const boundaryAt = (x: number, y: number) => {
        const radius = Math.hypot(x - C, y - C);
        if (radius < INNER_R - GRAB_SLOP_INNER || radius > OUTER_R + GRAB_SLOP_OUTER) return null;
        const minutes = minutesAt(x, y);
        let best: number | null = null;
        let bestGap = Infinity;
        for (let n = 1; n <= 4; n += 1) {
            const raw = Math.abs(intervalToMinutes(settings[`EndMode${n}`]) - minutes);
            const gap = Math.min(raw, 1440 - raw);
            if (gap < bestGap) { bestGap = gap; best = n; }
        }
        return bestGap <= GRAB_MINUTES ? best : null;
    };

    const onPointerDown = (e: React.PointerEvent<SVGSVGElement>) => {
        if (!editable) return;
        const { x, y } = toView(e);
        const index = boundaryAt(x, y);
        if (index == null) return;
        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);
        setDragging(index);
    };

    const onPointerMove = (e: React.PointerEvent<SVGSVGElement>) => {
        if (!editable) return;
        const { x, y } = toView(e);
        if (dragging == null) {
            setHovering(boundaryAt(x, y));
            return;
        }
        // Angle only while dragging: the pointer naturally wanders off the band.
        const next = moveBoundary(settings, dragging, minutesAt(x, y), snapMinutes);
        if (next !== settings) onChange(next);
    };

    const endDrag = () => setDragging(null);

    const parts = slices(settings);
    const centreMinutes = dragging != null ? intervalToMinutes(settings[`EndMode${dragging}`]) : null;
    const grabbable = editable && (dragging != null || hovering != null);

    return (
        <div className="eco-clock">
            <span className="eco-clock-mark">MIDNIGHT</span>
            <svg
                ref={svgRef}
                className={`eco-clock-face ${editable ? 'editable' : ''} ${grabbable ? 'grab' : ''} ${dragging != null ? 'dragging' : ''}`}
                viewBox={`0 0 ${VIEW} ${VIEW}`}
                role="img"
                aria-label="24-hour schedule"
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={endDrag}
                onPointerCancel={endDrag}
                onPointerLeave={() => setHovering(null)}
            >
                {HOUR_MARKS.map((mark) => {
                    const p = polar(LABEL_R, mark.minutes);
                    return (
                        <text key={mark.minutes} className="eco-clock-hour" x={p.x} y={p.y} textAnchor="middle" dominantBaseline="central">
                            {mark.label}
                        </text>
                    );
                })}

                {parts.map((slice) => (
                    <path key={slice.index} className={`eco-slice ${TONE_FOR_MODE[slice.mode] ?? 'off'}`} d={ringSlice(slice.start, slice.end)} />
                ))}

                {editable && parts.map((slice) => {
                    const inner = polar(PIN_SHAFT_INNER, slice.end);
                    const outer = polar(PIN_SHAFT_OUTER, slice.end);
                    const head = polar(OUTER_R, slice.end);
                    const grow = dragging === slice.index ? 2 : hovering === slice.index ? 1 : 0;
                    return (
                        <g key={`pin-${slice.index}`} className="eco-pin">
                            <line className="eco-pin-shaft" x1={inner.x} y1={inner.y} x2={outer.x} y2={outer.y} />
                            <circle className="eco-pin-outer" cx={head.x} cy={head.y} r={PIN_OUTER + grow} />
                            <circle className="eco-pin-inner" cx={head.x} cy={head.y} r={PIN_INNER + grow} />
                        </g>
                    );
                })}

                {centreMinutes !== null && (
                    <text className="eco-clock-centre" x={C} y={C} textAnchor="middle" dominantBaseline="central">
                        {formatTime(centreMinutes)}
                    </text>
                )}
            </svg>
            <span className="eco-clock-mark">NOON</span>
        </div>
    );
};

export default EconomyClock;
